import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';
import { compScheduleSetSelectable, CompensationServiceError, syncObPlans } from '@/services/compensationService';

const log = createComponentLogger('ObSelectablePlansPanel', 'compensation');

interface ScheduleRow {
  schedule_id: number;
  schedule_name: string;
  schedule_type: string | null;
  compensation_percentage: number | null;
  compensation_dollar: number | null;
  compensation_max_cap: number | null;
  is_active: boolean;
  is_selectable_for_lo_self_service: boolean;
  dynamic_comp_rules_json: unknown[] | null;
}

interface SyncRunRow {
  scope: 'plans_only' | 'full';
  trigger_source: 'manual' | 'system';
  status: 'succeeded' | 'failed';
  ran_at: string;
  schedule_count: number | null;
  error_message: string | null;
  actor: {
    first_name: string | null;
    last_name: string | null;
    email: string | null;
  } | null;
}

function isStructurallyEligible(row: ScheduleRow): boolean {
  return (
    row.schedule_type === 'Static' &&
    row.compensation_percentage != null &&
    (row.compensation_dollar == null || row.compensation_dollar === 0) &&
    (!Array.isArray(row.dynamic_comp_rules_json) || row.dynamic_comp_rules_json.length === 0)
  );
}

function formatPct(n: number | null): string {
  return n == null ? '—' : `${n}%`;
}

function formatMoney(n: number | null): string {
  if (n == null) {
return 'No cap';
}
  if (n === 0) {
return 'No cap';
}
  return `$${n.toLocaleString()}`;
}

function formatActorName(run: SyncRunRow | null): string {
  if (!run || run.trigger_source === 'system') {
return 'System';
}
  const fullName = [run.actor?.first_name, run.actor?.last_name].filter(Boolean).join(' ').trim();
  return fullName || run.actor?.email || 'Unknown admin';
}

function formatRunTimestamp(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
return value;
}
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}

export function ObSelectablePlansPanel(): JSX.Element {
  const [schedules, setSchedules] = useState<ScheduleRow[]>([]);
  const [latestRun, setLatestRun] = useState<SyncRunRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [pendingToggle, setPendingToggle] = useState<number | null>(null);

  const load = async () => {
    setLoading(true);
    const [scheduleResult, latestRunResult] = await Promise.all([
      supabase
        .from('ob_comp_schedules')
        .select('schedule_id, schedule_name, schedule_type, compensation_percentage, compensation_dollar, compensation_max_cap, is_active, is_selectable_for_lo_self_service, dynamic_comp_rules_json')
        .order('schedule_name'),
      supabase
        .from('ob_comp_sync_runs')
        .select('scope, trigger_source, status, ran_at, schedule_count, error_message, actor:users!ob_comp_sync_runs_requested_by_fkey(first_name, last_name, email)')
        .in('scope', ['plans_only', 'full'])
        .order('ran_at', { ascending: false })
        .limit(1)
        .maybeSingle()
    ]);

    if (scheduleResult.error) {
      log.error('Failed to load ob_comp_schedules', {}, scheduleResult.error);
      toast.error('Failed to load OB schedules');
      setLoading(false);
      return;
    }

    if (latestRunResult.error) {
      log.warn('Failed to load latest ob_comp_sync_runs row', {}, latestRunResult.error);
      setLatestRun(null);
    } else {
      setLatestRun((latestRunResult.data ?? null) as SyncRunRow | null);
    }

    setSchedules((scheduleResult.data ?? []) as ScheduleRow[]);
    setLoading(false);
  };

  useEffect(() => {
    void load();
  }, []);

  const handleToggle = async (row: ScheduleRow, next: boolean) => {
    setPendingToggle(row.schedule_id);
    try {
      await compScheduleSetSelectable(row.schedule_id, next);
      toast.success(next ? `${row.schedule_name} is now selectable for LOs` : `${row.schedule_name} is no longer selectable`);
      await load();
    } catch (e) {
      const msg = e instanceof CompensationServiceError ? e.message : (e as Error).message;
      toast.error(`Toggle failed: ${msg}`);
      log.error('compScheduleSetSelectable failed', { scheduleId: row.schedule_id }, e as Error);
    } finally {
      setPendingToggle(null);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await syncObPlans();
      toast.success(`Updated ${result.schedulesCount} OB ${result.schedulesCount === 1 ? 'plan' : 'plans'}`);
      await load();
    } catch (e) {
      const msg = e instanceof CompensationServiceError ? e.message : (e as Error).message;
      toast.error(`Update failed: ${msg}`);
      log.error('syncObPlans failed', {}, e as Error);
      await load();
    } finally {
      setSyncing(false);
    }
  };

  const latestRunLabel = latestRun == null
    ? 'No OB plan refresh recorded yet.'
    : latestRun.status === 'failed'
      ? `Last update failed by ${formatActorName(latestRun)} on ${formatRunTimestamp(latestRun.ran_at)}`
      : `Last updated by ${formatActorName(latestRun)} on ${formatRunTimestamp(latestRun.ran_at)}`;

  const latestRunDetail = latestRun?.schedule_count != null
    ? `${latestRun.schedule_count} ${latestRun.schedule_count === 1 ? 'plan' : 'plans'} refreshed`
    : null;

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <CardTitle>OB Schedules — Selectable for LO Self-Service</CardTitle>
        </div>
        <div className="flex flex-col items-start gap-1 sm:items-end">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleSync}
            disabled={syncing}
            className="self-start sm:self-auto"
          >
            {syncing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            {syncing ? 'Updating…' : 'Update'}
          </Button>
          <p className={`text-sm ${latestRun?.status === 'failed' ? 'text-destructive' : 'text-muted-foreground'}`}>
            {latestRunLabel}
          </p>
          {latestRunDetail && latestRun?.status !== 'failed' && (
            <p className="text-xs text-muted-foreground">{latestRunDetail}</p>
          )}
          {latestRun?.status === 'failed' && latestRun.error_message && (
            <p className="text-xs text-destructive">{latestRun.error_message}</p>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-4">
          Toggle whether each OB compensation schedule is offered to loan officers in the self-service "Change Plan"
          dialog. Only Static, percentage-based schedules with no dollar cap and no dynamic rules are eligible — the
          server enforces this contract via a CHECK constraint on <code>ob_comp_schedules</code>.
        </p>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : schedules.length === 0 ? (
          <p className="text-sm text-muted-foreground">No OB schedules synced yet. Run <code>ob-comp-sync</code>.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>%</TableHead>
                <TableHead>Max Cap</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Eligible?</TableHead>
                <TableHead className="text-right">Selectable</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {schedules.map((row) => {
                const eligible = isStructurallyEligible(row);
                return (
                  <TableRow key={row.schedule_id}>
                    <TableCell className="font-mono text-xs">{row.schedule_id}</TableCell>
                    <TableCell>{row.schedule_name}</TableCell>
                    <TableCell>
                      <Badge variant={row.schedule_type === 'Static' ? 'default' : 'secondary'}>
                        {row.schedule_type ?? '—'}
                      </Badge>
                    </TableCell>
                    <TableCell>{formatPct(row.compensation_percentage)}</TableCell>
                    <TableCell>{formatMoney(row.compensation_max_cap)}</TableCell>
                    <TableCell>
                      <Badge variant={row.is_active ? 'default' : 'outline'}>
                        {row.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={eligible ? 'default' : 'destructive'}>
                        {eligible ? 'Yes' : 'No'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Switch
                        checked={row.is_selectable_for_lo_self_service}
                        disabled={!eligible || !row.is_active || pendingToggle === row.schedule_id}
                        onCheckedChange={(v) => handleToggle(row, v)}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
